/**
 * Hook for assigning/removing a tag on an entire DocumentGroup. Separate
 * from useDocumentGrouping since a tag is its own backend model, not a
 * DocumentGroup, and (unlike the earlier range-based design) applies to a
 * group's whole range, so there's nothing to validate client-side.
 *
 * Assigning a tag no longer starts extraction by itself. That happens on
 * demand from the Generate Document dialog instead, see
 * GenerateCombinedPdfButton.
 */
import { KaboodleToaster } from "components/common";
import {
  DOCUMENT_GROUP_TAG_LABELS,
  useCreateDocumentGroupTagMutation,
  useDeleteDocumentGroupTagMutation,
  type DocumentGroupTagType,
} from "services/document-group-tag-api";

import * as Sentry from "@sentry/react";

export const useDocumentGroupTagAssignment = () => {
  const [createDocumentGroupTag] = useCreateDocumentGroupTagMutation();
  const [deleteDocumentGroupTag] = useDeleteDocumentGroupTagMutation();

  const assignDocumentGroupTag = async (
    documentGroupId: number,
    type: DocumentGroupTagType,
  ): Promise<boolean> => {
    const label = DOCUMENT_GROUP_TAG_LABELS[type];
    try {
      await createDocumentGroupTag({
        document_group: documentGroupId,
        type,
      }).unwrap();

      KaboodleToaster.success(`${label} assigned`);

      return true;
    } catch (error) {
      Sentry.captureException(error, {
        tags: { feature: "metadoc", action: "assign_document_group_tag" },
        extra: { documentGroupId, type },
      });
      KaboodleToaster.error(`Failed to assign ${label}. Please try again.`);
      return false;
    }
  };

  const removeDocumentGroupTag = async (
    documentGroupTagId: number,
    type: DocumentGroupTagType,
  ): Promise<void> => {
    const label = DOCUMENT_GROUP_TAG_LABELS[type];
    try {
      await deleteDocumentGroupTag(documentGroupTagId).unwrap();
      KaboodleToaster.success(`${label} removed`);
    } catch (error) {
      Sentry.captureException(error, {
        tags: { feature: "metadoc", action: "remove_document_group_tag" },
        extra: { documentGroupTagId, type },
      });
      KaboodleToaster.error(`Failed to remove ${label}. Please try again.`);
    }
  };

  return { assignDocumentGroupTag, removeDocumentGroupTag };
};
